import useBlowFish from "@ipru-common/hooks/useBlowFish";
import { cleanAndParseJSON, getSalaryRange, readQueryParams } from "../utils/CommonFunctions";
import { APP_KEY_BLOWFISH2 } from "@ipru-common/constants/SiteConstants";
import {
  DEFAULT,
  OPTION_VALUES,
  educationMapper as educationMapperQData,
  occupationMapper,
} from "@ipru-common/constants/InputValues";
import { findSalaryRange, formatDob } from "@ipru-common/utils/QuoteCommonFunctions";
import { getAge } from "@ipru-common/libs/ValidationSchemas/CustomRules";

export const mapInputToOutput = (inputData, uid) => {
  return {
    AnnualIncome: parseInt(inputData.yr_income),
    age: inputData.age,
    dob: inputData.user_dob,
    gender: inputData.gender,
    MobileNo: inputData.mobileno,
    EmailId: inputData.emailid,
    Education: inputData.calc_education,
    Occupation: inputData.calc_occupation,
    p_gender: inputData.propGender,
    p_dob: inputData.child_dob,
    trai: inputData.trai_flag,
    suitCheckbox: inputData.skip_suitability_check,
    traiFlag: inputData.trai_flag ? "YES" : "NO",
    relation: inputData.relation,
    Pincode: inputData.pincode,
    City: inputData.city,
    propAge: inputData.propAge,
    propDob: inputData.child_dob,
    propGender: inputData.propGender,
    LDUID: uid || 480,
    p_age: inputData.propAge,
    childName: inputData.child_name,
    attainsAge: inputData.attainsAge,
    mode: inputData.mode,
    invest: inputData.invest,
    duration: inputData.duration,
    policyTerm: inputData.policyTerm,
    fullName: inputData.full_name,
    backToPageURL: inputData?.backToPageURL,
    isCustomSavingPage: inputData?.isCustomSavingPage,
    nri: inputData?.nri || DEFAULT.ips_calculator.nri,
    agentCode: inputData?.agentCode || "",
    LMS_ID: inputData?.LMS_ID || "",
    stop_quote_redirect: inputData?.stop_quote_redirect ?? false,
  };
};

export const mapOutputToInput = (outputData) => {
  let salaryKey = "";
  const salaryRange = getSalaryRange();
  const income = outputData?.AnnualIncome?.toString() || outputData?.Annual_Income_Bracket?.toString();
  if (income && income !== "undefined") {
    const { key } = findSalaryRange(income, salaryRange);
    salaryKey = key || "";
  }
  return {
    yr_income: salaryKey,
    age: getAge(outputData?.dob?.toString()) || outputData.age || outputData.Age,
    user_dob: formatDob(outputData.dob),
    gender: outputData.gender || outputData.Gender || OPTION_VALUES.GENDER.MALE,
    mobileno: outputData.MobileNo || outputData.Mobile || outputData.Mobile_Number,
    emailid: outputData.EmailId || outputData.Email || outputData.Email_ID,
    calc_education:
      educationMapper(outputData.Education) ||
      educationMapperQData[outputData.Education] ||
      OPTION_VALUES.EDUCATION.GRADUATE,
    calc_occupation:
      outputData.Occupation || occupationMapper[outputData.Occupation] || OPTION_VALUES.OCCUPATION.SALARIED,
    // propGender: outputData.p_gender,
    child_dob: formatDob(outputData.cdob) || formatDob(outputData.p_dob) || formatDob(outputData.child_dob),
    trai_flag: outputData.trai || outputData?.trai_flag || true,
    skip_suitability_check: outputData.suitCheckbox,
    relation: outputData.relation || DEFAULT.signature_ulip.relation,
    pincode: outputData.pincode || outputData?.Pincode,
    city: outputData.City,
    propAge: outputData.propAge,
    propDob: formatDob(outputData.propDob),
    propGender: outputData.propGender,
    LDUID: outputData.LDUID,
    // propAge: outputData.p_age,
    child_name: outputData?.childName || outputData?.cName || outputData.child_name || "",
    attainsAge: outputData?.attainsAge,
    policyTerm: outputData?.term,
    full_name: outputData.fullName || outputData.Full_Name,
    backToPageURL: outputData?.backToPageURL,
    LMS_ID: outputData?.LMS_ID,
    productId: outputData?.productId,
    agentCode: outputData?.agentCode,
    isCustomSavingPage: outputData?.isCustomSavingPage,
    nri: outputData?.nri || DEFAULT.ips_calculator.nri,
    stop_quote_redirect: outputData?.stop_quote_redirect ?? false,
  };
};

export const educationMapper = (education) => {
  const mapping = {
    676: OPTION_VALUES.EDUCATION.GRADUATE,
    680: OPTION_VALUES.EDUCATION.GRADUATE,
    679: OPTION_VALUES.EDUCATION.TWELVE_DIPLOMA,
    675: OPTION_VALUES.EDUCATION.TWELVE_DIPLOMA,
    678: OPTION_VALUES.EDUCATION.BELOW_TWELVE,
    677: OPTION_VALUES.EDUCATION.BELOW_TWELVE,
  };

  return mapping[education] || null; // returns null if no match
};

export const useQData = () => {
  const { encryptBlowfish, decryptBlowfishQData } = useBlowFish({ key: APP_KEY_BLOWFISH2 });

  const handleDataEncryption = (inputData) => {
    const queryParams = readQueryParams();
    const uid = queryParams.get("UID");

    const mappedData = mapInputToOutput(inputData, uid);
    const formDataJson = JSON.stringify(mappedData);
    const encrypted = encryptBlowfish(formDataJson);
    const eQdata = encodeURIComponent(encrypted);
    return eQdata;
  };
  const handleDataDecryption = (encryptedQdata) => {
    try {
      const str = decryptBlowfishQData(encryptedQdata.replace(/ /g, "+"));
      const cleanData = cleanAndParseJSON(str);
      const inputData = mapOutputToInput(cleanData);
      return inputData;
    } catch (error) {
      console.error("Error decrypting or mapping data:", error);
      return null;
    }
  };

  const decryptAndMapBackToInput = (encryptedQdata) => {
    return handleDataDecryption(encryptedQdata);
  };

  return {
    handleDataEncryption,
    decryptAndMapBackToInput,
  };
};
